import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Container,
    Button,
    Divider,
    Grid,
    Paper,
    Chip,
    IconButton,
    useTheme,
    useMediaQuery,
    Breadcrumbs,
    Link as MuiLink,
    Card,
    CardContent,
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LaunchIcon from '@mui/icons-material/Launch';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutomationIcon from '@mui/icons-material/AutoAwesome';
import OutputIcon from '@mui/icons-material/Output';
import useSEO from "../../hooks/useSEO";
import integrations from "../../data/templates.json";

// Component for the share dialog/popup
const ShareDialog = ({ open, handleClose, templateName }) => {

    const [copySuccess, setCopySuccess] = useState(false);
    const currentUrl = window.location.href;

    const handleCopy = () => {
        navigator.clipboard.writeText(currentUrl);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
    };

    if (!open) return null;

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000,
            }}
            onClick={handleClose}
        >
            <Card
                sx={{
                    width: '90%',
                    maxWidth: '500px',
                    backgroundColor: 'rgba(35, 22, 66, 0.95)',
                    color: 'white',
                    p: 3,
                    borderRadius: 2,
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <Typography variant="h6" fontWeight="bold" mb={2}>
                    Share {templateName}
                </Typography>
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    p: 2,
                    borderRadius: 1,
                    mb: 2
                }}>
                    <Typography sx={{ flexGrow: 1, mr: 2, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {currentUrl}
                    </Typography>
                    <Button 
                        variant="contained" 
                        onClick={handleCopy}
                        sx={{
                            backgroundColor: '#e95f43',
                            '&:hover': {
                                backgroundColor: '#d25539',
                            }
                        }}
                    >
                        {copySuccess ? 'Copied!' : 'Copy'}
                    </Button>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button onClick={handleClose} sx={{ color: 'white' }}>
                        Close
                    </Button>
                </Box>
            </Card>
        </Box>
    );
};

const TemplateDetailPage = () => {
    const { templateId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [shareDialogOpen, setShareDialogOpen] = useState(false);

    // Check if template data was passed via location state
    const passedTemplate = location.state?.template;
    
    // Find the template with the matching name or use passed template
    const template = passedTemplate || integrations.find(item => 
        item.name.toLowerCase().replace(/\s+/g, '-') === templateId
    );
	useSEO({
			title: "Tools - Beanbag AI",
			description: "Tools Description",
			canonical: "https://www.beanbag.ai/",
			keywords: "AI, artificial intelligence, automation, business solutions, workflow optimization",
			
			ogTitle: "Beanbag AI",
			ogDescription: "Low-code Agentic AI Platform to Automate Workflows",
			ogImage: "https://www.beanbag.ai/images/beanbag-social-card.jpg",
			ogUrl: "https://www.beanbag.ai/",
			ogType: "website",
			
			twitterCard: "summary_large_image",
			twitterSite: "@BeanbagAI",
			
			jsonLd: {
				"@context": "https://schema.org",
				"@type": "Organization",
				"name": "Beanbag AI",
				"url": "https://www.beanbag.ai",
				"logo": "https://www.beanbag.ai/logo192.png",
				"description": "Beanbag AI provides intelligent automation solutions to enhance productivity and streamline workflows."
			}
		});

    useEffect(() => {
        // If template not found, redirect to marketplace
        if (!template && templateId) {
            navigate('/templates');
        }
        
        // Scroll to top when component mounts
        window.scrollTo(0, 0);
    }, [template, templateId, navigate]);

    // If template is not found and we're still here, show loading
    if (!template) {
        return (
            <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
                <Typography variant="h4" color="white">Loading...</Typography>
            </Container>
        );
    }

    

    // Handle share button click
    const handleShareClick = () => {
        setShareDialogOpen(true);
    };

    // Handle close share dialog
    const handleCloseShareDialog = () => {
        setShareDialogOpen(false);
    };

    // Sample data for "How it works" section
    const howItWorks = [
        {
            title: "Connect your account",
            description: "First, connect your account to the template by entering your API key or authorizing via OAuth."
        },
        {
            title: "Configure inputs",
            description: template.input || "Set up the required parameters and data sources that will be used by this template."
        },
        {
            title: "Run the workflow",
            description: "With everything configured, simply hit 'Run' and let the automation do its work."
        },
        {
            title: "Review outcomes",
            description: template.outcomes || "Analyze the results and make data-driven decisions based on the insights provided."
        }
    ];

    return (
        <Container maxWidth="lg" sx={{ py: 4, marginTop: { xs: "60px", sm: "60px", md: "80px", lg: "100px" } }}>
            {/* Breadcrumbs */}
            <Breadcrumbs 
                separator={<NavigateNextIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.5)' }} />} 
                aria-label="breadcrumb"
                sx={{ 
                    mb: 4, 
                    color: 'rgba(255,255,255,0.7)',
                    '& .MuiBreadcrumbs-ol': {
                        flexWrap: 'wrap'
                    }
                }}
            >
                <MuiLink 
                    color="inherit" 
                    href="/" 
                    sx={{ 
                        textDecoration: 'none',
                        color: 'rgba(255,255,255,0.7)',
                        '&:hover': {
                            color: 'white',
                            textDecoration: 'underline'
                        }
                    }}
                >
                    Home
                </MuiLink>
                <MuiLink 
                    color="inherit" 
                    href="/templates" 
                    sx={{ 
                        textDecoration: 'none',
                        color: 'rgba(255,255,255,0.7)',
                        '&:hover': {
                            color: 'white',
                            textDecoration: 'underline'
                        }
                    }}
                >
                    Templates
                </MuiLink>
                <Typography color="white">{template.name}</Typography>
            </Breadcrumbs>

            {/* Back Button (Mobile) */}
            {isMobile && (
                <Button
                    startIcon={<ArrowBackIcon />}
                    sx={{ 
                        color: 'white', 
                        mb: 2,
                        pl: 0,
                        '&:hover': {
                            backgroundColor: 'transparent',
                            opacity: 0.8
                        }
                    }}
                    onClick={() => navigate('/integrations')}
                >
                    Back to Integrations
                </Button>
            )}

            {/* Main Content */}
            <Grid container spacing={4}>
                {/* Left Column - Template Info */}
                <Grid item xs={12} md={8}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 4 }}>
                        {/* Template Logo */}
                        <Box
                            sx={{
                                width: { xs: 80, sm: 100, md: 120 },
                                height: { xs: 80, sm: 100, md: 120 },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 2,
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                fontSize: { xs: '32px', sm: '40px', md: '48px' },
                                mr: 3,
                                flexShrink: 0
                            }}
                        >
                            {template.logo}
                        </Box>
                        
                        {/* Template Title and Category */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                <Typography
                                    variant="h3"
                                    fontWeight="bold"
                                    color="white"
                                    sx={{
                                        fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
                                        lineHeight: 1.2,
                                        mr: 1
                                    }}
                                >
                                    {template.name}
                                </Typography>
                                <Chip 
                                    label={template.category} 
                                    size={isMobile ? "small" : "medium"}
                                    sx={{ 
                                        backgroundColor: 'rgba(233, 95, 67, 0.2)',
                                        color: 'white',
                                        fontSize: { xs: '0.75rem', sm: '0.85rem' },
                                    }}
                                />
                            </Box>
                            
                            {/* Description */}
                            <Typography
                                variant="body1"
                                color="white"
                                sx={{
                                    opacity: 0.9,
                                    fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                                    lineHeight: 1.6,
                                    mb: 2
                                }}
                            >
                                {template.description}
                            </Typography>
                        </Box>
                    </Box>

                    {/* How It Works Section */}
                    <Paper
                        elevation={0}
                        sx={{
                            backgroundColor: '#13171c',
                            color: 'white',
                            p: { xs: 2, sm: 3, md: 4 },
                            borderRadius: { xs: 2, md: 3 },
                            mb: 4
                        }}
                    >
                        <Typography
                            variant="h5"
                            fontWeight="bold"
                            sx={{
                                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                                mb: 3
                            }}
                        >
                            How It Works
                        </Typography>

                        <Grid container spacing={3}>
                            {howItWorks.map((step, index) => (
                                <Grid item xs={12} sm={6} key={index}>
                                    <Box
                                        sx={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                            p: { xs: 2, md: 1 },
                                            borderRadius: 2,
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                            <Box
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: '50%',
                                                    backgroundColor: '#e95f43',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: 'bold',
                                                    mr: 2,
                                                    flexShrink: 0
                                                }}
                                            >
                                                {index + 1}
                                            </Box>
                                            <Typography
                                                variant="h6"
                                                fontWeight="bold"
                                                sx={{ fontSize: { xs: '1rem', md: '1.15rem' } }}
                                            >
                                                {step.title}
                                            </Typography>
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                opacity: 0.9,
                                                fontSize: { xs: '0.85rem', md: '0.95rem' },
                                                lineHeight: 1.6
                                            }}
                                        >
                                            {step.description}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>

                    {/* Input and Outcomes Details (if available) */}
                    {(template.input || template.outcomes) && (
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            {template.input && (
                                <Grid item xs={12} sm={6}>
                                    <Card
                                        sx={{
                                            backgroundColor: '#13171c',
                                            color: 'white',
                                            height: '100%'
                                        }}
                                    >
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <InfoOutlinedIcon sx={{ mr: 1, color: '#e95f43' }} />
                                                <Typography variant="h6" fontWeight="bold">
                                                    Input Requirements
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                                                {template.input}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}
                            
                            {template.outcomes && (
                                <Grid item xs={12} sm={6}>
                                    <Card
                                        sx={{
                                            backgroundColor: '#13171c',
                                            color: 'white',
                                            height: '100%'
                                        }}
                                    >
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <OutputIcon sx={{ mr: 1, color: '#e95f43' }} />
                                                <Typography variant="h6" fontWeight="bold">
                                                    Expected Outcomes
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                                                {template.outcomes}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </Grid>

                {/* Right Column - Action Card */}
                <Grid item xs={12} md={4}>
                    <Box
                        sx={{
                            position: { md: 'sticky' },
                            top: { md: 100 }
                        }}
                    >
                        <Paper
                            elevation={0}
                            sx={{
                                backgroundColor: '#13171c',
                                color: 'white',
                                p: { xs: 2.5, sm: 3, md: 4 },
                                borderRadius: { xs: 2, md: 3 },
                                border: '1px solid rgba(255, 255, 255, 0.1)'
                            }}
                        >
                            <Typography
                                variant="h5"
                                fontWeight="bold"
                                sx={{
                                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                                    mb: 3
                                }}
                            >
                                Get Started
                            </Typography>

                            <Button
                                variant="contained"
                                endIcon={<LaunchIcon />}
                                fullWidth
                                sx={{
                                    backgroundColor: '#e95f43',
                                    color: 'white',
                                    p: { xs: 1.25, sm: 1.5 },
                                    borderRadius: 6,
                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                    fontWeight: 'bold',
                                    mb: 2,
                                    '&:hover': {
                                        backgroundColor: '#d25539',
                                    }
                                }}
                                href={template.Link || "#"}
                                target={template.Link ? "_blank" : "_self"}
                            >
                                Use Template
                            </Button>

                            <Button
                                variant="outlined"
                                startIcon={<ShareIcon />}
                                fullWidth
                                onClick={handleShareClick}
                                sx={{
                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                    color: 'white',
                                    p: { xs: 1.25, sm: 1.5 },
                                    borderRadius: 6,
                                    '&:hover': {
                                        borderColor: 'white',
                                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                    }
                                }}
                            >
                                Share
                            </Button>

                            <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                            {/* Template Stats */}
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="subtitle2" color="rgba(255, 255, 255, 0.7)" sx={{ mb: 0.5 }}>
                                    Category
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {template.category}
                                </Typography>
                            </Box>
                            
                            {/* More stats can be added here */}
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="subtitle2" color="rgba(255, 255, 255, 0.7)" sx={{ mb: 0.5 }}>
                                    Last Updated
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {template.lastUpdated || "May 10, 2025"}
                                </Typography>
                            </Box>
                            
                            <Box>
                                <Typography variant="subtitle2" color="rgba(255, 255, 255, 0.7)" sx={{ mb: 0.5 }}>
                                    Pricing
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {template.pricing || "Free"}
                                </Typography>
                            </Box>

                            {/* Related Templates - Optional section */}
                            <Box sx={{ mt: 4 }}>
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                                    Related Templates
                                </Typography>
                                {integrations
                                    .filter(item => item.category === template.category && item.name !== template.name)
                                    .slice(0, 3)
                                    .map((relatedTemplate, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                py: 1.5,
                                                borderBottom: index < 2 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 30,
                                                    height: 30,
                                                    borderRadius: 1,
                                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '16px',
                                                    mr: 2,
                                                }}
                                            >
                                                {relatedTemplate.logo}
                                            </Box>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        color: '#e95f43',
                                                    }
                                                }}
                                                onClick={() => navigate(`/integrations/${relatedTemplate.name.toLowerCase().replace(/\s+/g, '-')}`)}
                                            >
                                                {relatedTemplate.name}
                                            </Typography>
                                        </Box>
                                    ))}
                            </Box>
                        </Paper>
                    </Box>
                </Grid>
            </Grid>

            {/* Share Dialog */}
            <ShareDialog 
                open={shareDialogOpen} 
                handleClose={handleCloseShareDialog} 
                templateName={template.name} 
            />
        </Container>
    );
};

export default TemplateDetailPage;